import { useRef, useEffect, useState, memo } from 'react'
import { useEditorStore, PRESET_BACKGROUNDS } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'
import { motion, AnimatePresence, usePresence } from 'framer-motion'
import { interpolateCursorPosition, drawCursorOnCanvas } from '@/lib/cursor-renderer'
import { computeZoomAtTime } from '@/lib/zoom-suggestions'
import { VolumeHighIcon as Volume2, VolumeMute01Icon as VolumeX, Move01Icon as Move } from 'hugeicons-react'

// ── Helper functions for sizing the video card proportionally ─────────────────
const parseAspectRatio = (ratioStr: string): number => {
    if (!ratioStr) return 16 / 9
    const parts = ratioStr.split('/')
    if (parts.length === 2) {
        const w = parseFloat(parts[0])
        const h = parseFloat(parts[1])
        if (w > 0 && h > 0) return w / h
    }
    const val = parseFloat(ratioStr)
    return isNaN(val) || val <= 0 ? 16 / 9 : val
}

const getCardDimensions = (
    canvasWidth: number,
    canvasHeight: number,
    padding: number,
    videoNaturalRatio: number | null,
    videoCrop: { x: number; y: number; width: number; height: number } | null,
    aspectRatio: string
) => {
    const availW = canvasWidth - 2 * padding
    const availH = canvasHeight - 2 * padding
    if (availW <= 0 || availH <= 0) {
        return { cardWidth: 0, cardHeight: 0, cardOffsetX: 0, cardOffsetY: 0, cardRatio: 16 / 9 }
    }

    let cardRatio = 16 / 9
    if (videoNaturalRatio) {
        cardRatio = videoNaturalRatio
    } else if (videoCrop) {
        cardRatio = videoCrop.width / videoCrop.height
    } else {
        cardRatio = parseAspectRatio(aspectRatio)
    }

    let cardWidth = 0
    let cardHeight = 0
    const areaRatio = availW / availH

    if (cardRatio > areaRatio) {
        cardWidth = availW
        cardHeight = availW / cardRatio
    } else {
        cardHeight = availH
        cardWidth = availH * cardRatio
    }

    const cardOffsetX = (availW - cardWidth) / 2
    const cardOffsetY = (availH - cardHeight) / 2

    return { cardWidth, cardHeight, cardOffsetX, cardOffsetY, cardRatio }
}

// ── Zoom Viewport Overlay ─────────────────────────────────────────────────────
// Isolated component so zoom drag updates don't re-render the heavy CanvasPreview
const ZoomViewportOverlay = memo(function ZoomViewportOverlay({
    canvasSize,
    videoNaturalRatio,
}: {
    canvasSize: { width: number; height: number }
    videoNaturalRatio: number | null
}) {
    const isPlaying    = useEditorStore(state => state.isPlaying)
    const currentTime  = useEditorStore(state => state.currentTime)
    const zoomRegions  = useEditorStore(state => state.zoomRegions)
    const padding      = useEditorStore(state => state.padding)
    const videoCrop    = useEditorStore(state => state.videoCrop)
    const aspectRatio  = useEditorStore(state => state.aspectRatio)
    const updateZoomRegion = useEditorStore(state => state.updateZoomRegion)

    const zoomDragRef = useRef<{
        type: 'move' | 'resize'
        startMouseX: number; startMouseY: number
        startRectLeft: number; startRectTop: number
        startScale: number
        // resize: fixed corner (opposite to grabbed) in avail-area px
        fixedCornerX: number; fixedCornerY: number
        // resize: position of grabbed corner at mousedown, in avail-area px
        startDraggedX: number; startDraggedY: number
        availW: number; availH: number
        id: string
    } | null>(null)
    const [isDraggingZoom, setIsDraggingZoom] = useState(false)

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDraggingZoom || !zoomDragRef.current) return
            const drag = zoomDragRef.current
            const dpx = e.clientX - drag.startMouseX
            const dpy = e.clientY - drag.startMouseY

            if (drag.type === 'move') {
                const rW = drag.availW / drag.startScale
                const rH = drag.availH / drag.startScale
                const newRectLeft = Math.max(0, Math.min(drag.availW - rW, drag.startRectLeft + dpx))
                const newRectTop  = Math.max(0, Math.min(drag.availH - rH, drag.startRectTop  + dpy))
                const newCx = (newRectLeft + rW / 2) / drag.availW
                const newCy = (newRectTop  + rH / 2) / drag.availH
                updateZoomRegion(drag.id, {
                    x: Math.max(-50, Math.min(50, (newCx - 0.5) * 100)),
                    y: Math.max(-50, Math.min(50, (newCy - 0.5) * 100)),
                })
            } else {
                // Diagonal-distance resize — preserves aspect ratio naturally
                const curDraggedX = drag.startDraggedX + dpx
                const curDraggedY = drag.startDraggedY + dpy
                const origDist = Math.hypot(
                    drag.startDraggedX - drag.fixedCornerX,
                    drag.startDraggedY - drag.fixedCornerY,
                )
                const newDist = Math.hypot(
                    curDraggedX - drag.fixedCornerX,
                    curDraggedY - drag.fixedCornerY,
                )
                if (newDist < 5) return
                const newScale = Math.max(1.1, Math.min(5, drag.startScale * (origDist / newDist)))
                const newRectW = drag.availW / newScale
                const newRectH = drag.availH / newScale
                // Keep fixed corner pinned; recompute rect left/top
                const newRectLeft = drag.fixedCornerX === drag.startRectLeft
                    ? drag.fixedCornerX
                    : drag.fixedCornerX - newRectW
                const newRectTop = drag.fixedCornerY === drag.startRectTop
                    ? drag.fixedCornerY
                    : drag.fixedCornerY - newRectH
                const clampedLeft = Math.max(0, Math.min(drag.availW - newRectW, newRectLeft))
                const clampedTop  = Math.max(0, Math.min(drag.availH - newRectH, newRectTop))
                const newCx = (clampedLeft + newRectW / 2) / drag.availW
                const newCy = (clampedTop  + newRectH / 2) / drag.availH
                updateZoomRegion(drag.id, {
                    scale: newScale,
                    x: Math.max(-50, Math.min(50, (newCx - 0.5) * 100)),
                    y: Math.max(-50, Math.min(50, (newCy - 0.5) * 100)),
                })
            }
        }
        const handleMouseUp = () => {
            setIsDraggingZoom(false)
            zoomDragRef.current = null
            document.body.style.cursor = 'default'
        }
        if (isDraggingZoom) {
            window.addEventListener('mousemove', handleMouseMove)
            window.addEventListener('mouseup', handleMouseUp)
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isDraggingZoom, updateZoomRegion])

    if (isPlaying || !canvasSize.width) return null
    const activeZoom = zoomRegions.find(r => currentTime >= r.startTime && currentTime <= r.endTime)
    if (!activeZoom) return null

    const { cardWidth, cardHeight, cardOffsetX, cardOffsetY } = getCardDimensions(
        canvasSize.width,
        canvasSize.height,
        padding,
        videoNaturalRatio,
        videoCrop,
        aspectRatio
    )
    if (cardWidth <= 0 || cardHeight <= 0) return null

    const scale  = activeZoom.scale
    const cx = 0.5 + activeZoom.x / 100
    const cy = 0.5 + activeZoom.y / 100
    const rectW = cardWidth / scale
    const rectH = cardHeight / scale
    const rawLeft  = cx * cardWidth - rectW / 2
    const rawTop   = cy * cardHeight - rectH / 2
    const rectLeft = Math.max(0, Math.min(cardWidth - rectW, rawLeft))
    const rectTop  = Math.max(0, Math.min(cardHeight - rectH, rawTop))
    const absLeft  = padding + cardOffsetX + rectLeft
    const absTop   = padding + cardOffsetY + rectTop

    const isPanMode   = activeZoom.mode === 'pan'
    const accentColor = isPanMode ? '#3b82f6' : '#a78bfa'
    const RX = 8  // border-radius in px

    const handleMoveMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation(); e.preventDefault()
        zoomDragRef.current = {
            type: 'move',
            startMouseX: e.clientX, startMouseY: e.clientY,
            startRectLeft: rectLeft, startRectTop: rectTop,
            startScale: scale,
            fixedCornerX: 0, fixedCornerY: 0,
            startDraggedX: 0, startDraggedY: 0,
            availW: cardWidth, availH: cardHeight, id: activeZoom.id,
        }
        setIsDraggingZoom(true)
        document.body.style.cursor = 'grabbing'
    }

    const handleCornerMouseDown = (e: React.MouseEvent, corner: 'tl' | 'tr' | 'bl' | 'br') => {
        e.stopPropagation(); e.preventDefault()
        const r = { l: rectLeft, t: rectTop, r: rectLeft + rectW, b: rectTop + rectH }
        const fixedX = (corner === 'tl' || corner === 'bl') ? r.r : r.l
        const fixedY = (corner === 'tl' || corner === 'tr') ? r.b : r.t
        const dragX  = (corner === 'tl' || corner === 'bl') ? r.l : r.r
        const dragY  = (corner === 'tl' || corner === 'tr') ? r.t : r.b
        const cur    = (corner === 'tl' || corner === 'br') ? 'nwse-resize' : 'nesw-resize'
        zoomDragRef.current = {
            type: 'resize',
            startMouseX: e.clientX, startMouseY: e.clientY,
            startRectLeft: rectLeft, startRectTop: rectTop,
            startScale: scale,
            fixedCornerX: fixedX, fixedCornerY: fixedY,
            startDraggedX: dragX, startDraggedY: dragY,
            availW: cardWidth, availH: cardHeight, id: activeZoom.id,
        }
        setIsDraggingZoom(true)
        document.body.style.cursor = cur
    }

    const maskId = `zm-${activeZoom.id.slice(0, 8)}`
    const cornerHandles = [
        { corner: 'tl' as const, x: absLeft - 5,         y: absTop - 5,         cur: 'nwse-resize' },
        { corner: 'tr' as const, x: absLeft + rectW - 6,  y: absTop - 5,         cur: 'nesw-resize' },
        { corner: 'bl' as const, x: absLeft - 5,          y: absTop + rectH - 6, cur: 'nesw-resize' },
        { corner: 'br' as const, x: absLeft + rectW - 6,  y: absTop + rectH - 6, cur: 'nwse-resize' },
    ]

    return (
        <>
            {/* SVG dark overlay with transparent rounded viewport cutout */}
            <svg
                className="absolute z-[35] pointer-events-none"
                style={{
                    left: padding + cardOffsetX,
                    top: padding + cardOffsetY,
                    width: cardWidth,
                    height: cardHeight,
                    overflow: 'visible'
                }}
            >
                <defs>
                    <mask id={maskId}>
                        <rect width={cardWidth} height={cardHeight} fill="white" />
                        <rect x={rectLeft} y={rectTop} width={rectW} height={rectH} rx={RX} ry={RX} fill="black" />
                    </mask>
                </defs>
                {/* Dark surround with hole */}
                <rect width={cardWidth} height={cardHeight} fill="rgba(0,0,0,0.55)" mask={`url(#${maskId})`} />
                {/* Viewport border */}
                <rect
                    x={rectLeft} y={rectTop} width={rectW} height={rectH}
                    rx={RX} ry={RX}
                    fill="none"
                    stroke={accentColor}
                    strokeWidth={2}
                    filter={`drop-shadow(0 0 6px ${accentColor}80)`}
                />
            </svg>

            {/* Draggable move target (covers viewport interior) */}
            <div
                className="absolute z-[36] pointer-events-auto flex items-center justify-center"
                style={{
                    left: absLeft, top: absTop, width: rectW, height: rectH,
                    cursor: isDraggingZoom ? 'grabbing' : 'grab',
                    borderRadius: RX,
                }}
                onMouseDown={handleMoveMouseDown}
            >
                <Move size={14} style={{ color: accentColor, opacity: 0.7, pointerEvents: 'none' }} />
                <div
                    className="absolute text-[9px] font-semibold px-1.5 py-0.5 rounded pointer-events-none select-none"
                    style={{ top: -22, left: 0, background: accentColor, color: '#fff', whiteSpace: 'nowrap' }}
                >
                    {isPanMode ? 'PAN' : 'FOCUS'} {scale.toFixed(1)}×
                </div>
            </div>

            {/* Corner resize handles */}
            {cornerHandles.map(({ corner, x, y, cur }) => (
                <div
                    key={corner}
                    className="absolute z-[37] w-3 h-3"
                    style={{
                        left: x, top: y,
                        background: accentColor,
                        border: '1.5px solid rgba(255,255,255,0.7)',
                        borderRadius: 3,
                        cursor: cur,
                    }}
                    onMouseDown={(e) => handleCornerMouseDown(e, corner)}
                />
            ))}
        </>
    )
})

const ElementLayerRenderer = memo(function ElementLayerRenderer({
    l,
    selectedElementLayerId,
    handleElementMouseDown,
}: {
    l: any
    selectedElementLayerId: string | null
    handleElementMouseDown: (e: React.MouseEvent, id: string) => void
}) {
    const isSelected = l.id === selectedElementLayerId

    return (
        <div
            className="absolute pointer-events-auto"
            style={{
                left: `${l.x}%`,
                top: `${l.y}%`,
                transform: `translate(-50%, -50%) rotate(${l.rotation}deg)`,
                zIndex: isSelected ? 55 : 45,
                opacity: l.opacity / 100,
            }}
            onMouseDown={(e) => handleElementMouseDown(e, l.id)}
        >
            <div className={`cursor-move relative ${isSelected ? 'ring-2 ring-brand-500 rounded-sm ring-offset-2 ring-offset-transparent' : ''}`} style={{ width: `${l.width}px`, height: `${l.height}px`, minWidth: '20px', minHeight: '20px' }}>
                {l.type === 'rectangle' && (
                    <div style={{ width: '100%', height: '100%', border: `${l.strokeWidth}px solid ${l.color}`, backgroundColor: 'transparent' }} />
                )}
                {l.type === 'ellipse' && (
                    <div style={{ width: '100%', height: '100%', border: `${l.strokeWidth}px solid ${l.color}`, borderRadius: '50%', backgroundColor: 'transparent' }} />
                )}
                {l.type === 'arrow' && (
                    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
                        <defs>
                            <marker id={`arrowhead-${l.id}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill={l.color} />
                            </marker>
                        </defs>
                        <line x1="0" y1="50" x2="100" y2="50" stroke={l.color} strokeWidth={l.strokeWidth} markerEnd={`url(#arrowhead-${l.id})`} />
                    </svg>
                )}
                {l.type === 'callout' && (
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        backgroundColor: l.color,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: l.colorText,
                        fontFamily: l.fontFamily,
                        fontSize: `${l.fontSize}px`,
                        padding: '8px',
                        textAlign: 'center',
                        border: `${l.strokeWidth}px solid rgba(255,255,255,0.2)`
                    }}>
                        {l.content}
                        <div style={{
                            position: 'absolute',
                            bottom: '-10px',
                            left: '20px',
                            width: 0,
                            height: 0,
                            borderLeft: '10px solid transparent',
                            borderRight: '10px solid transparent',
                            borderTop: `10px solid ${l.color}`
                        }} />
                    </div>
                )}
            </div>
        </div>
    )
})

const TextLayerRenderer = memo(function TextLayerRenderer({
    l,
    currentTime,
    selectedTextLayerId,
    textLayers,
    handleTextMouseDown,
}: {
    l: any
    currentTime: number
    selectedTextLayerId: string | null
    textLayers: any[]
    handleTextMouseDown: (e: React.MouseEvent, id: string) => void
}) {
    const [isPresent] = usePresence()
    const isAnotherLayerActive = textLayers.some(
        other => other.id !== l.id && currentTime >= other.startTime && currentTime <= other.endTime
    )

    // If this layer is exiting but another layer has already started, instantly hide it to prevent subtitle overlaps
    if (!isPresent && isAnotherLayerActive) {
        return null
    }

    return (
        <div
            className="absolute pointer-events-auto"
            style={{
                left: `${l.x}%`,
                top: `${l.y}%`,
                transform: `translate(${l.textAlign === 'left' ? '0' : l.textAlign === 'right' ? '-100%' : '-50%'}, -50%)`,
                zIndex: l.id === selectedTextLayerId ? 60 : 50,
            }}
        >
            <motion.div
                initial={(() => {
                    switch (l.animationIn) {
                        case 'pop': return { scale: 0.5, opacity: 0 }
                        case 'fade': return { opacity: 0 }
                        case 'slide-up': return { y: 30, opacity: 0 }
                        case 'slide-down': return { y: -30, opacity: 0 }
                        case 'slide-left': return { x: 30, opacity: 0 }
                        case 'slide-right': return { x: -30, opacity: 0 }
                        case 'zoom-in': return { scale: 1.35, opacity: 0 }
                        case 'blur-in': return { filter: 'blur(8px)', opacity: 0 }
                        case 'bounce': return { y: -20, opacity: 0 }
                        case 'flip-x': return { scaleX: 0, opacity: 0 }
                        default: return false
                    }
                })()}
                animate={{
                    scale: 1,
                    scaleX: 1,
                    opacity: l.opacity,
                    x: 0,
                    y: 0,
                    filter: 'blur(0px)',
                }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={l.animationIn === 'bounce'
                    ? { type: 'spring', damping: 6, stiffness: 200, mass: 0.8 }
                    : l.animationIn === 'flip-x'
                        ? { type: 'spring', damping: 18, stiffness: 280, duration: 0.4 }
                        : {
                            type: 'spring',
                            damping: 20,
                            stiffness: 200,
                            duration: 0.5
                        }
                }
                onMouseDown={(e) => handleTextMouseDown(e, l.id)}
            >
                <div
                    key={`${l.id}-${l.startTime}`}
                    className={`cursor-move select-none ${l.id === selectedTextLayerId ? 'ring-2 ring-brand-500 rounded-lg' : ''}`}
                    style={{
                        fontSize: `${l.fontSize}px`,
                        color: l.textGradient ? 'transparent' : l.color,
                        backgroundImage: l.textGradient || 'none',
                        WebkitBackgroundClip: l.textGradient ? 'text' : 'none',
                        display: l.textGradient ? 'inline-block' : 'block',
                        fontFamily: l.fontFamily,
                        fontWeight: l.fontWeight as any,
                        textAlign: l.textAlign,
                        letterSpacing: `${l.letterSpacing}px`,
                        lineHeight: l.lineHeight,
                        textDecoration: l.textDecoration,
                        textTransform: l.textTransform,
                        backgroundColor: l.backgroundColor
                            ? `rgba(${parseInt(l.backgroundColor.slice(1, 3), 16)}, ${parseInt(l.backgroundColor.slice(3, 5), 16)}, ${parseInt(l.backgroundColor.slice(5, 7), 16)}, ${l.backgroundOpacity ?? 1})`
                            : 'transparent',
                        padding: l.backgroundColor ? `${l.backgroundPadding}px` : '0px',
                        borderRadius: `${l.backgroundBorderRadius}px`,
                        whiteSpace: 'pre-wrap',
                        textShadow: (l.backgroundColor || l.textGradient) ? 'none' : (l.textShadow || '0 2px 4px rgba(0,0,0,0.3)'),
                        WebkitTextStroke: l.strokeEnabled ? `${l.strokeWidth || 2}px ${l.strokeColor || '#000000'}` : '0px transparent',
                        width: 'max-content',
                    }}
                >
                    {(() => {
                        if (l.animationIn === 'typewriter') {
                            const elapsed = currentTime - l.startTime
                            const twDur = Math.min(l.content.length * 0.05, 2.0)
                            const chars = elapsed >= twDur
                                ? l.content.length
                                : Math.floor((elapsed / twDur) * l.content.length)
                            return l.content.slice(0, Math.max(0, chars)) || '\u00a0'
                        }
                        return l.content || ' '
                    })()}
                </div>
            </motion.div>
        </div>
    )
})

interface CanvasPreviewProps {
    videoBlob: Blob
    cameraBlob?: Blob | null
}

function hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r},${g},${b},${alpha})`
}

export default function CanvasPreview({ videoBlob, cameraBlob }: CanvasPreviewProps) {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const cameraVideoRef = useRef<HTMLVideoElement | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const videoContentRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLDivElement>(null)
    const cursorCanvasRef = useRef<HTMLCanvasElement>(null)

    // Subscribe to store updates
    const {
        backgroundType,
        backgroundValue,
        backgroundBlur,
        backgroundDim,
        backgroundNoise,
        trackVisibility,
        padding,
        borderRadius,
        shadow,
        perspectiveSettings,
        clipContent,
        borderWidth,
        borderColor,
        borderOpacity,
        animationIn,
        animationOut,
        duration,
        setDuration,
        initClips,
        aspectRatio,
        videoCrop,
        textLayers,
        elementLayers,
        currentTime,
        updateTextLayer,
        updateElementLayer,
        setSelectedTextLayer,
        setSelectedElementLayer,
        selectedTextLayerId,
        selectedElementLayerId,
        cameraLayer,
        updateCameraLayer,
        cursorSettings,
        recordingVolume,
        recordingMuted,
    } = useEditorStore()

    const [draggingTextId, setDraggingTextId] = useState<string | null>(null)
    const [draggingElementId, setDraggingElementId] = useState<string | null>(null)
    const [isDraggingCamera, setIsDraggingCamera] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null)
    const [cameraVideoEl, setCameraVideoEl] = useState<HTMLVideoElement | null>(null)
    const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map())
    // Track video's natural aspect ratio so the card matches the video exactly —
    // no black bars (contain) and no cropping (cover).
    const [videoNaturalRatio, setVideoNaturalRatio] = useState<number | null>(null)

    const syncVideoMetadata = (video: HTMLVideoElement) => {
        let videoDur = video.duration
        console.log("Sync Video Metadata:", { duration: videoDur, videoWidth: video.videoWidth, videoHeight: video.videoHeight })

        // If metadata is Infinity, fallback to recording store duration
        if (!Number.isFinite(videoDur)) {
            const recDur = useRecordingStore.getState().duration
            if (recDur > 0) {
                videoDur = recDur
                console.log("Using Recording Store Duration Fallback:", { duration: videoDur })
            }
        }

        // Only update if duration is finite
        if (Number.isFinite(videoDur) && videoDur > 0) {
            const currentDur = useEditorStore.getState().duration
            if (currentDur === 0) {
                setDuration(videoDur)
                initClips(videoDur, !!cameraBlob)

                // Auto-set aspect ratio from actual video dimensions on first load
                const vw = video.videoWidth
                const vh = video.videoHeight
                if (vw > 0 && vh > 0) {
                    const g = (a: number, b: number): number => b === 0 ? a : g(b, a % b)
                    const d = g(vw, vh)
                    useEditorStore.getState().setAspectRatio(`${vw / d}/${vh / d}`)
                }
            }
        }

        // Always capture the video's natural ratio so the card matches it exactly
        if (video.videoWidth > 0 && video.videoHeight > 0) {
            const ratio = video.videoWidth / video.videoHeight
            if (videoNaturalRatio !== ratio) {
                setVideoNaturalRatio(ratio)
            }
        }
    }

    // Robustly sync natural video aspect ratio on mount / update / state changes
    useEffect(() => {
        if (videoEl && videoEl.readyState >= 1) {
            syncVideoMetadata(videoEl)
        }
    }, [videoEl, videoNaturalRatio])

    // Update store with duration data
    const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        syncVideoMetadata(e.currentTarget)
    }

    const getCardStyle = () => {
        const { rotateX, rotateY, rotateZ, scale: pScale, perspective: pDepth } = perspectiveSettings
        const canvasSize = getCanvasSize()
        const { cardWidth, cardHeight } = getCardDimensions(
            canvasSize.width,
            canvasSize.height,
            padding,
            videoNaturalRatio,
            videoCrop,
            aspectRatio
        )

        return {
            transform: `perspective(${pDepth}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotateZ(${rotateZ}deg) scale(${pScale})`,
            transformStyle: 'preserve-3d' as const,
            borderRadius: `${borderRadius}px`,
            boxShadow: [
                borderWidth > 0 ? `0 0 0 ${borderWidth}px ${hexToRgba(borderColor, borderOpacity)}` : null,
                shadow !== 'none' ? shadow : null,
            ].filter(Boolean).join(', ') || 'none',
            backgroundColor: '#000',
            width: `${cardWidth}px`,
            height: `${cardHeight}px`,
        }
    }

    // Sync Play/Pause and Seek
    useEffect(() => {
        let animationFrameId: number
        let lastTime = performance.now()

        const updateLoop = () => {
            const now = performance.now()
            const delta = (now - lastTime) / 1000
            lastTime = now

            const state = useEditorStore.getState()
            const { isPlaying, currentTime, duration, clips, setCurrentTime, zoomRegions, setIsPlaying } = state

            // 1. Advance Time (Master Clock)
            if (isPlaying) {
                const newTime = currentTime + delta

                if (newTime >= duration) {
                    setCurrentTime(duration)
                    setIsPlaying(false)
                } else {
                    setCurrentTime(newTime)
                }
            }

            // 2. Sync Video (Slave)
            if (videoRef.current) {
                const time = state.currentTime // refetch in case updated above
                // Find visible clip for this Timeline Time
                const activeClip = clips.find(c => {
                    const spd = c.speed ?? 1
                    const clipDur = (c.end - c.start) / spd
                    return c.status === 'visible' && time >= c.offset && time < c.offset + clipDur
                })

                if (activeClip) {
                    const spd = activeClip.speed ?? 1
                    // Calculate Source Time (accounting for speed)
                    const sourceTime = activeClip.start + (time - activeClip.offset) * spd
                    
                    // drift check (play state aware tolerance, do not interrupt ongoing seek)
                    const tolerance = isPlaying ? 1.0 : 0.15
                    if (videoRef.current.readyState >= 1 && !videoRef.current.seeking && Math.abs(videoRef.current.currentTime - sourceTime) > tolerance) {
                        try {
                            videoRef.current.currentTime = sourceTime
                        } catch (e) {
                            console.warn("Failed to seek main video:", e)
                        }
                    }

                    // Apply playback rate
                    try {
                        if (videoRef.current.playbackRate !== spd) {
                            videoRef.current.playbackRate = spd
                        }
                    } catch (e) {
                        console.warn("Failed to set playbackRate on main video:", e)
                    }

                    // Play if needed (and we are playing timeline)
                    try {
                        if (isPlaying && videoRef.current.paused) {
                            videoRef.current.play().catch(() => { })
                        } else if (!isPlaying && !videoRef.current.paused) {
                            videoRef.current.pause()
                        }
                    } catch (e) {
                        console.warn("Failed to change play state on main video:", e)
                    }

                    if (videoContentRef.current) videoContentRef.current.style.opacity = '1'
                } else {
                    // GAP
                    try {
                        if (!videoRef.current.paused) videoRef.current.pause()
                    } catch (e) {
                        console.warn("Failed to pause main video in gap:", e)
                    }
                    if (videoContentRef.current) videoContentRef.current.style.opacity = '0'
                }

                // Sync Camera Video
                const camRange = cameraLayer.timeRange
                const cameraActiveAtTime = cameraLayer.visible && state.trackVisibility.camera && (!camRange || (time >= camRange.start && time <= camRange.end))
                if (cameraVideoRef.current && cameraLayer.visible && state.trackVisibility.camera) {
                    const camDuration = cameraVideoRef.current.duration
                    const targetTime = (camDuration && camDuration > 0) ? (time % camDuration) : time
                    const camTolerance = isPlaying ? 1.0 : 0.15
                    if (cameraVideoRef.current.readyState >= 1 && !cameraVideoRef.current.seeking && Math.abs(cameraVideoRef.current.currentTime - targetTime) > camTolerance) {
                        try {
                            cameraVideoRef.current.currentTime = targetTime
                        } catch (e) {
                            console.warn("Failed to seek camera video:", e)
                        }
                    }
                    try {
                        if (isPlaying && cameraActiveAtTime && cameraVideoRef.current.paused) {
                            cameraVideoRef.current.play().catch(() => { })
                        } else if ((!isPlaying || !cameraActiveAtTime) && !cameraVideoRef.current.paused) {
                            cameraVideoRef.current.pause()
                        }
                    } catch (e) {
                        console.warn("Failed to change play state on camera video:", e)
                    }
                }

                // Sync Audio Layers
                const { audioLayers } = useEditorStore.getState()
                const audioMap = audioElementsRef.current
                // Create/remove audio elements as layers change
                for (const [id, el] of audioMap.entries()) {
                    try {
                        if (!audioLayers.find(l => l.id === id)) { el.pause(); audioMap.delete(id) }
                    } catch (e) {
                        console.warn("Failed to pause discarded audio layer:", e)
                    }
                }
                for (const layer of audioLayers) {
                    if (!audioMap.has(layer.id) && layer.url) {
                        const el = new Audio(layer.url)
                        el.volume = layer.volume ?? 1
                        audioMap.set(layer.id, el)
                    }
                    const el = audioMap.get(layer.id)
                    if (!el) continue
                    const isMutedByTrack = layer.type === 'music' ? !state.trackVisibility.music : !state.trackVisibility.sfx
                    el.volume = (layer.muted || isMutedByTrack) ? 0 : (layer.volume ?? 1)
                    const audioTime = time - layer.offset
                    const isActive = audioTime >= 0 && audioTime < layer.duration
                    if (isActive) {
                        const sourceTime = audioTime + (layer.trimStart ?? 0)
                        const audioTolerance = isPlaying ? 1.0 : 0.15
                        if (el.readyState >= 1 && !el.seeking && Math.abs(el.currentTime - sourceTime) > audioTolerance) {
                            try {
                                el.currentTime = sourceTime
                            } catch (e) {
                                console.warn("Failed to seek audio layer:", e)
                            }
                        }
                        try {
                            if (isPlaying && el.paused) el.play().catch(() => {})
                            else if (!isPlaying && !el.paused) el.pause()
                        } catch (e) {
                            console.warn("Failed to change play state on audio layer:", e)
                        }
                    } else {
                        try {
                            if (!el.paused) el.pause()
                        } catch (e) {
                            console.warn("Failed to pause audio layer:", e)
                        }
                    }
                }

                // Get cursor position early so zoom can follow it
                const recState = useRecordingStore.getState()
                const mouseData = recState.mouseData
                const cs = useEditorStore.getState().cursorSettings
                
                let cursorNormalizedPos: { x: number; y: number } | undefined = undefined
                let pos: any = undefined
                
                if (mouseData && mouseData.length > 0) {
                    pos = interpolateCursorPosition(
                        mouseData,
                        time * 1000,
                        recState.screenWidth,
                        recState.screenHeight,
                        cs.smoothing
                    )
                    if (pos) {
                        cursorNormalizedPos = { x: pos.x, y: pos.y }
                    }
                }

                // 3. Zoom Logic (Recordly timing model via computeZoomAtTime)
                // When paused: show unzoomed video + viewport rectangle overlay (like Recordly)
                // When playing: apply zoom transform, no overlay
                const activeZoomRegion = zoomRegions.find(r => time >= r.startTime && time <= r.endTime)
                const suppressZoom = !isPlaying && !!activeZoomRegion

                const currentZoom = suppressZoom
                    ? { scale: 1, x: 0, y: 0 }
                    : computeZoomAtTime(time, zoomRegions, cursorNormalizedPos)

                if (videoContentRef.current) {
                    videoContentRef.current.style.transform = `scale(${currentZoom.scale}) translate(${currentZoom.x}%, ${currentZoom.y}%)`
                }

                // 4. Cursor overlay rendering
                if (cs.visible && cursorCanvasRef.current) {
                    const cCanvas = cursorCanvasRef.current
                    const cCtx = cCanvas.getContext('2d')
                    if (cCtx) {
                        const rect = cCanvas.getBoundingClientRect()
                        // Match canvas internal resolution to displayed size (for crisp rendering)
                        const dpr = window.devicePixelRatio || 1
                        if (cCanvas.width !== Math.round(rect.width * dpr) || cCanvas.height !== Math.round(rect.height * dpr)) {
                            cCanvas.width = Math.round(rect.width * dpr)
                            cCanvas.height = Math.round(rect.height * dpr)
                        }
                        cCtx.clearRect(0, 0, cCanvas.width, cCanvas.height)
                        cCtx.save()
                        cCtx.scale(dpr, dpr)

                        // Skip drawing cursor shape for extension (cursor baked in).
                        // Desktop uses uiohook data and should draw full overlay.
                        const skipCursorShape = recState.isExtension && !recState.isDesktop
                        if (pos) {
                            // ── Cursor position mapping ──────────────────────────────────────
                            // Pipeline: screen-normalized → div-normalized → zoom → canvas px
                            //
                            // The card (videoContentRef parent) fills the padded area exactly
                                // (CSS width/height: 100% in flex with align-items:center makes
                                // the card = availW × availH, aspect-ratio ignored when both dims set).
                                // The video inside uses object-fit:cover, which may crop top/bottom
                                // or left/right if the card ratio ≠ video ratio.
                                // Zoom (scale S, translate tx/ty%) is applied to videoContentRef (the
                                // div, not the video element), so it operates in div-normalized space.

                                const { padding: padPx } = state

                                // Video's natural aspect ratio from the actual element
                                const vEl = videoRef.current
                                const vRatio = (vEl && vEl.videoWidth > 0 && vEl.videoHeight > 0)
                                    ? vEl.videoWidth / vEl.videoHeight
                                    : 16 / 9

                                const { cardWidth, cardHeight, cardOffsetX, cardOffsetY, cardRatio } = getCardDimensions(
                                    rect.width,
                                    rect.height,
                                    padPx,
                                    videoNaturalRatio,
                                    state.videoCrop,
                                    state.aspectRatio
                                )

                                // How object-fit:cover renders the video in the card:
                                let rendW: number, rendH: number, offX = 0, offY = 0
                                if (cardRatio >= vRatio) {
                                    // Card wider → fill width, overflow/crop height
                                    rendW = cardWidth
                                    rendH = cardWidth / vRatio
                                    offY = (rendH - cardHeight) / 2
                                } else {
                                    // Card taller → fill height, overflow/crop width
                                    rendH = cardHeight
                                    rendW = cardHeight * vRatio
                                    offX = (rendW - cardWidth) / 2
                                }

                                // Step 1: screen-normalized → div-normalized (undo crop offset)
                                const toDivNorm = (sx: number, sy: number) => ({
                                    dx: cardWidth > 0 ? (sx * rendW - offX) / cardWidth : sx,
                                    dy: cardHeight > 0 ? (sy * rendH - offY) / cardHeight : sy,
                                })

                                // Step 2: apply zoom (CSS scale(S) translate(tx%,ty%) in div space)
                                const zS = currentZoom.scale
                                const zTx = currentZoom.x / 100
                                const zTy = currentZoom.y / 100
                                const applyZoom = (dx: number, dy: number) => ({
                                    zx: 0.5 + zS * (dx + zTx - 0.5),
                                    zy: 0.5 + zS * (dy + zTy - 0.5),
                                })

                                // Step 3: div-normalized → canvas-normalized (add padding)
                                const toCanvas = (zx: number, zy: number) => ({
                                    cx: (padPx + cardOffsetX + zx * cardWidth) / rect.width,
                                    cy: (padPx + cardOffsetY + zy * cardHeight) / rect.height,
                                })

                                const compose = (sx: number, sy: number) => {
                                    const { dx, dy } = toDivNorm(sx, sy)
                                    const { zx, zy } = applyZoom(dx, dy)
                                    return toCanvas(zx, zy)
                                }

                                const { cx: finalX, cy: finalY } = compose(pos.x, pos.y)
                                const { cx: finalClickX, cy: finalClickY } = compose(pos.lastClickX, pos.lastClickY)

                                const mappedPos = {
                                    ...pos,
                                    x: finalX,
                                    y: finalY,
                                    lastClickX: finalClickX,
                                    lastClickY: finalClickY,
                                }

                                drawCursorOnCanvas(
                                    cCtx,
                                    mappedPos,
                                    cs,
                                    rect.width,
                                    rect.height,
                                    skipCursorShape
                                )
                            }
                        cCtx.restore()
                    }
                }
            }

            animationFrameId = requestAnimationFrame(updateLoop)
        }

        animationFrameId = requestAnimationFrame(updateLoop)
        return () => cancelAnimationFrame(animationFrameId)
    }, [cameraLayer.visible, videoNaturalRatio]) // Re-run if camera visibility or video ratio changes

    // Handle Text / Camera / Zoom Dragging
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (draggingTextId && canvasRef.current) {
                const rect = canvasRef.current.getBoundingClientRect()
                let x = ((e.clientX - rect.left) / rect.width) * 100
                let y = ((e.clientY - rect.top) / rect.height) * 100
                x = Math.max(-10, Math.min(110, x))
                y = Math.max(-10, Math.min(110, y))
                updateTextLayer(draggingTextId, { x, y })
            } else if (isDraggingCamera && canvasRef.current) {
                const rect = canvasRef.current.getBoundingClientRect()
                let x = ((e.clientX - rect.left) / rect.width) * 100
                let y = ((e.clientY - rect.top) / rect.height) * 100
                x = Math.max(0, Math.min(100, x))
                y = Math.max(0, Math.min(100, y))
                updateCameraLayer({ x, y })
            }
        }

        const handleMouseUp = () => {
            // Resume Zundo history tracking
            const temporal = useEditorStore.temporal.getState()
            temporal.resume()

            // Trigger final updates to commit the complete drag as a single state entry
            setDraggingTextId(null)
            setDraggingElementId(null)
            setIsDraggingCamera(false)
            document.body.style.cursor = 'default'
        }

        if (draggingTextId || isDraggingCamera) {
            window.addEventListener('mousemove', handleMouseMove)
            window.addEventListener('mouseup', handleMouseUp)
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [draggingTextId, draggingElementId, isDraggingCamera, updateTextLayer, updateElementLayer, updateCameraLayer])

    const handleTextMouseDown = (e: React.MouseEvent, id: string) => {
        e.stopPropagation()
        e.preventDefault()
        
        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        setSelectedTextLayer(id)
        setDraggingTextId(id)
        document.body.style.cursor = 'move'
    }

    const handleElementMouseDown = (e: React.MouseEvent, id: string) => {
        e.preventDefault()
        setSelectedElementLayer(id)
        setDraggingElementId(id)
        e.stopPropagation()
    }

    const handleCameraMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()

        // Pause Zundo history tracking during drag
        useEditorStore.temporal.getState().pause()

        setIsDraggingCamera(true)
        document.body.style.cursor = 'move'
    }

    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = isMuted
        for (const el of audioElementsRef.current.values()) el.muted = isMuted
    }, [isMuted])

    useEffect(() => {
        if (videoRef.current) {
            const isMutedByTrack = !trackVisibility.video
            videoRef.current.muted = recordingMuted || isMutedByTrack
            videoRef.current.volume = (recordingMuted || isMutedByTrack) ? 0 : recordingVolume
        }
    }, [recordingVolume, recordingMuted, trackVisibility.video])

    // Cleanup audio elements on unmount
    useEffect(() => {
        return () => { for (const el of audioElementsRef.current.values()) el.pause() }
    }, [])

    useEffect(() => {
        let url = ''
        if (videoEl && videoBlob) {
            url = URL.createObjectURL(videoBlob)
            videoEl.src = url
            try {
                videoEl.load()
            } catch (e) {
                console.warn("Failed to load video source:", e)
            }
        }
        return () => {
            if (url) {
                URL.revokeObjectURL(url)
            }
            if (videoEl) {
                videoEl.src = ''
            }
        }
    }, [videoEl, videoBlob])

    useEffect(() => {
        let url = ''
        if (cameraVideoEl && cameraBlob) {
            url = URL.createObjectURL(cameraBlob)
            cameraVideoEl.src = url
            try {
                cameraVideoEl.load()
            } catch (e) {
                console.warn("Failed to load camera source:", e)
            }
        }
        return () => {
            if (url) {
                URL.revokeObjectURL(url)
            }
            if (cameraVideoEl) {
                cameraVideoEl.src = ''
            }
        }
    }, [cameraVideoEl, cameraBlob])

    const getWallpaperStyle = (): React.CSSProperties => {
        const style: React.CSSProperties = {
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: '#000',
            filter: backgroundBlur > 0 ? `blur(${backgroundBlur}px)` : 'none',
            transform: backgroundBlur > 0 ? 'scale(1.1)' : 'none', // Prevent edge peaking on blur
        }

        if (backgroundType === 'image' || backgroundValue.startsWith('http') || backgroundValue.startsWith('blob:') || backgroundValue.startsWith('/')) {
            style.backgroundImage = `url("${backgroundValue}")`
        } else if (backgroundValue.includes('gradient')) {
            style.background = backgroundValue
            style.backgroundSize = 'cover'
            style.backgroundPosition = 'center'
            style.backgroundRepeat = 'no-repeat'
            style.filter = backgroundBlur > 0 ? `blur(${backgroundBlur}px)` : 'none'
            style.transform = backgroundBlur > 0 ? 'scale(1.1)' : 'none'
        } else {
            style.backgroundImage = 'none'
            style.backgroundColor = backgroundValue
        }

        return style
    }

    const getAnimationClass = () => {
        if (duration > 1 && currentTime >= Math.max(0, duration - 0.6) && animationOut && animationOut !== 'none') {
            return `animate-anim-${animationOut}`
        }
        if (animationIn && animationIn !== 'none') {
            return `animate-anim-${animationIn}`
        }
        return ''
    }

    const getVideoStyle = (): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            opacity: trackVisibility.video ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
        }

        if (!videoCrop) {
            // The card already has the video's exact aspect ratio.
            // Use width/height 100% with objectFit fill — fills the card
            // edge-to-edge with zero black bars, preventing any sub-pixel cropping.
            return {
                ...baseStyle,
                width: '100%',
                height: '100%',
                objectFit: 'fill'
            }
        }

        const sx = 100 / videoCrop.width
        const sy = 100 / videoCrop.height
        const centerX = videoCrop.x + videoCrop.width / 2
        const centerY = videoCrop.y + videoCrop.height / 2
        const tx = 50 - centerX
        const ty = 50 - centerY

        return {
            ...baseStyle,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${sx}, ${sy}) translate(${tx}%, ${ty}%)`,
            transformOrigin: 'center center'
        }
    }

    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

    useEffect(() => {
        if (!containerRef.current) return
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerSize({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                })
            }
        })
        observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [])

    const getCanvasSize = () => {
        if (!containerSize.width || !containerSize.height) return { width: 0, height: 0 }

        const [w, h] = aspectRatio.split('/').map(Number)
        const targetRatio = w / h
        const containerRatio = containerSize.width / containerSize.height

        if (containerRatio > targetRatio) {
            return {
                height: containerSize.height,
                width: containerSize.height * targetRatio
            }
        } else {
            return {
                width: containerSize.width,
                height: containerSize.width / targetRatio
            }
        }
    }

    const canvasSize = getCanvasSize()

    return (
        <div
            ref={containerRef}
            className="w-full h-full flex items-center justify-center bg-surface-950 overflow-hidden relative"
        >
            <button
                onClick={() => setIsMuted(m => !m)}
                className="absolute bottom-3 right-3 z-50 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white/60 hover:text-white hover:bg-black/80 transition-colors"
                title={isMuted ? 'Unmute preview' : 'Mute preview'}
            >
                {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <div
                ref={canvasRef}
                className={`relative shadow-2xl transition-all duration-300 ease-out bg-black ${clipContent ? 'overflow-hidden' : ''}`}
                style={{
                    width: `${canvasSize.width}px`,
                    height: `${canvasSize.height}px`,
                    cursor: undefined,
                }}
            >
                {/* Background Layer with Animation & Film Grain Noise Support */}
                {(() => {
                    const activePreset = PRESET_BACKGROUNDS.find(p => p.value === backgroundValue)
                    const animClass = activePreset?.animatedClass || ''
                    const isTexture = activePreset?.category === 'texture'
                    const effectiveNoise = backgroundNoise > 0 ? backgroundNoise : (isTexture ? 0.25 : 0)

                    return (
                        <>
                            <div
                                className={`absolute inset-0 z-0 transition-all duration-300 ease-out ${animClass}`}
                                style={getWallpaperStyle()}
                            />
                            {effectiveNoise > 0 && (
                                <div
                                    className="noise-overlay pointer-events-none z-[1]"
                                    style={{ opacity: effectiveNoise }}
                                />
                            )}
                        </>
                    )
                })()}

                {/* Dim Layer */}
                {backgroundDim > 0 && (
                    <div
                        className="absolute inset-0 z-[2] bg-black transition-opacity duration-300 pointer-events-none"
                        style={{ opacity: backgroundDim }}
                    />
                )}

                <div
                    className="absolute inset-0 z-10 flex items-center justify-center perspective-container"
                >
                    <div className="w-full h-full flex items-center justify-center">
                        <motion.div
                            key={`${animationIn}-${animationOut}`}
                            className={`relative flex items-center justify-center transition-all duration-300 ease-out will-change-transform ${getAnimationClass()}`}
                            style={getCardStyle()}
                            layout
                        >
                            <div
                                className="w-full h-full overflow-hidden bg-black"
                                style={{ borderRadius: `${borderRadius}px` }}
                            >
                                <div
                                    ref={videoContentRef}
                                    id="video-content-layer"
                                    className="w-full h-full flex items-center justify-center will-change-transform origin-center relative overflow-hidden"
                                    style={{ borderRadius: `${borderRadius}px` }}
                                >
                                    <video
                                        id="main-video-player"
                                        ref={(node) => {
                                            videoRef.current = node
                                            setVideoEl(node)
                                            if (node && node.readyState >= 1) {
                                                syncVideoMetadata(node)
                                            }
                                        }}
                                        style={getVideoStyle()}
                                        className=""
                                        controls={false}
                                        loop
                                        playsInline
                                        onLoadedMetadata={handleLoadedMetadata}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Zoom Viewport Overlay — isolated component to avoid re-rendering CanvasPreview on drag */}
                <ZoomViewportOverlay canvasSize={canvasSize} videoNaturalRatio={videoNaturalRatio} />

                {/* Camera Layer */}
                {cameraLayer.visible && trackVisibility.camera && cameraBlob && (!cameraLayer.timeRange || (currentTime >= cameraLayer.timeRange.start && currentTime <= cameraLayer.timeRange.end)) && (
                    <div
                        className="absolute z-40 pointer-events-auto cursor-move"
                        style={{
                            left: `${cameraLayer.x}%`,
                            top: `${cameraLayer.y}%`,
                            width: `${cameraLayer.size}%`,
                            aspectRatio: cameraLayer.mask === 'rectangle' ? '16/9' : '1/1',
                            transform: 'translate(-50%, -50%)',
                        }}
                        onMouseDown={handleCameraMouseDown}
                    >
                        <div
                            className="w-full h-full overflow-hidden"
                            style={{
                                borderRadius: cameraLayer.mask === 'circle' ? '50%' : `${cameraLayer.borderRadius ?? 35}%`,
                                border: `${cameraLayer.borderWidth}px solid ${cameraLayer.borderColor}`,
                                boxShadow: '0 10px 30px -5px rgba(0,0,0,0.5)',
                            }}
                        >
                            <video
                                ref={(node) => {
                                    cameraVideoRef.current = node
                                    setCameraVideoEl(node)
                                }}
                                autoPlay
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                )}

                {/* Cursor Overlay Canvas */}
                {cursorSettings.visible && (
                    <canvas
                        ref={cursorCanvasRef}
                        className="absolute inset-0 z-30 pointer-events-none"
                        style={{ width: '100%', height: '100%' }}
                    />
                )}

                {/* Text Layers (Moved to Top Level of Canvas) */}
                <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden">
                    <AnimatePresence>
                        {trackVisibility.text && textLayers
                            .filter(l => currentTime >= l.startTime && currentTime <= l.endTime)
                            .map(l => (
                                <TextLayerRenderer
                                    key={`${l.id}-${l.startTime}`}
                                    l={l}
                                    currentTime={currentTime}
                                    selectedTextLayerId={selectedTextLayerId}
                                    textLayers={textLayers}
                                    handleTextMouseDown={handleTextMouseDown}
                                />
                            ))}
                    </AnimatePresence>
                </div>

                {/* Element Layers */}
                <div className="absolute inset-0 z-[45] pointer-events-none overflow-hidden">
                    {useEditorStore.getState().trackVisibility.elements !== false && elementLayers
                        .filter(l => currentTime >= l.startTime && currentTime <= l.endTime)
                        .map(l => (
                            <ElementLayerRenderer
                                key={`${l.id}-${l.startTime}`}
                                l={l}
                                selectedElementLayerId={selectedElementLayerId}
                                handleElementMouseDown={handleElementMouseDown}
                            />
                        ))}
                </div>
            </div>
        </div>
    )
}
